import classNames from "classnames";
import React, { useState } from "react";
import { IPaneSwap, usePaneSwap } from "../../../shared/hooks/usePaneSwap";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";
import "./kuika-tab-content.scss";

/** Which way the tabs moved: `forward` = to a later tab in `order`, so the new pane comes in from the right. */
export type KuikaTabContentDirection = "forward" | "backward";

export interface IKuikaTabContentProps {
  /** The selected tab's value — usually the same value `KuikaTabs` holds. */
  activeKey: string;
  /**
   * One pane per tab, each keyed by its tab value (`<Settings key="settings" />`). Only the pane whose
   * key is `activeKey` is rendered; the others are not in the DOM — unless `keepMounted`.
   */
  children?: React.ReactNode;
  /** Alternative to keyed children: renders the pane for the active key. */
  render?: (activeKey: string) => React.ReactNode;
  /**
   * The tabs' values in their on-screen order. Given, a pane slides in from the side the selection
   * moved towards; without it, panes cross-fade with a small vertical lift.
   */
  order?: string[];
  /**
   * Keeps every pane mounted, for tabs that share one form: an antd `Form` around all of them reads
   * only the fields that are mounted (`getFieldsValue`), and a field that unmounts loses what was
   * typed into it.
   *
   * - Every keyed child is rendered, always; the inactive ones are `hidden` and `aria-hidden`, so
   *   they take no room, no focus and no reading, and keep their state and values.
   * - No pane is ever drawn twice: there is no leaver. The pane that becomes active comes in
   *   (`kuika-pane-in`, sideways with `order`); the one it replaces is hidden at once.
   * - The first render never animates; under reduced motion the incoming pane only fades in.
   * - With `render` instead of children, the panes are `render(key)` for every key in `order`,
   *   plus every key that has been active — so without `order` the panes shown so far stay mounted
   *   (the ones never shown mount on first view).
   */
  keepMounted?: boolean;
  className?: string;
}

interface IKeyedPane {
  key: string;
  node: React.ReactNode;
}

/**
 * A pane is found by its key, so a child without one is never drawn — and nothing says so. The
 * easy way to write one is a pane wrapped in a fragment or a condition (`{isIos && <Ios />}` with
 * the key on the inner element), which drops it silently. Development builds say it.
 */
const warnUnkeyedPane = (child: React.ReactElement): void => {
  if (process.env.NODE_ENV !== "development") return;
  const name: string = typeof child.type === "string" ? child.type : "a component";
  console.warn(
    `KuikaTabContent: a pane (${name}) has no key, so it is never shown. Give every pane the key of its tab, on the element passed as the child — not inside a fragment.`
  );
};

const findPane = (children: React.ReactNode, key: string): React.ReactNode => {
  let pane: React.ReactNode = null;

  // forEach hands over each child as written, key included (toArray would prefix the keys).
  React.Children.forEach(children, (child: React.ReactNode): void => {
    if (!React.isValidElement(child)) return;
    if (child.key === null) {
      warnUnkeyedPane(child);
      return;
    }
    if (child.key === key) {
      pane = child;
    }
  });

  return pane;
};

/** Every keyed child, in the order written — the panes `keepMounted` keeps. */
const keyedChildren = (children: React.ReactNode): IKeyedPane[] => {
  const panes: IKeyedPane[] = [];

  React.Children.forEach(children, (child: React.ReactNode): void => {
    if (!React.isValidElement(child)) return;
    if (child.key === null) {
      warnUnkeyedPane(child);
      return;
    }
    panes.push({ key: String(child.key), node: child });
  });

  return panes;
};

/**
 * `keepMounted` with `render`: a pane for every key in `order`, then for every key that has been
 * active and is not in `order` — the only keys `render` can be asked for.
 */
const renderedPanes = (
  render: (key: string) => React.ReactNode,
  order: string[] | undefined,
  shownKeys: string[]
): IKeyedPane[] => {
  const keys: string[] = [...(order ?? [])];
  shownKeys.forEach((key: string): void => {
    if (!keys.includes(key)) {
      keys.push(key);
    }
  });
  return keys.map((key: string): IKeyedPane => ({ key, node: render(key) }));
};

const directionOf = (order: string[] | undefined, fromKey: string, toKey: string): KuikaTabContentDirection | null => {
  const from: number = order?.indexOf(fromKey) ?? -1;
  const to: number = order?.indexOf(toKey) ?? -1;

  if (from === -1 || to === -1) {
    return null;
  }

  return to > from ? "forward" : "backward";
};

/**
 * The content under a `KuikaTabs`. When `activeKey` changes, the old pane leaves (`kuika-pane-out`,
 * laid over the new one, aria-hidden, no pointer) while the new pane arrives (`kuika-pane-in`), on
 * `--dur-base`. The frame takes the new pane's height at once — there is no height morph (that is
 * `KModalStepBody`, which shares the same `usePaneSwap`).
 *
 * - The frame fills its container's width, also in a column or grid that does not stretch its
 *   children (`align-items: flex-start` / `center`, `justify-items: start`) — no `width: 100%` at
 *   the call site. Content that should sit centred centres itself inside its pane.
 * - The first render never animates; only tab changes do.
 * - The inactive panes are not in the DOM, like the `{tab === "x" && <X />}` blocks this replaces —
 *   unless `keepMounted`, for tabs that share one form (see the prop).
 * - Switching again while a pane is leaving hands the leaver role to the pane shown until then.
 * - The leaving pane is rendered from the same children (or `render`), so every pane must be passed
 *   keyed — not wrapped in `{tab === "x" && …}` conditions of the caller's own.
 * - Reduced motion: no leaver — the new pane replaces the old at once and only fades in.
 */
const KuikaTabContent = ({
  activeKey,
  children,
  render,
  order,
  keepMounted = false,
  className
}: IKuikaTabContentProps): JSX.Element => {
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  // Every key that has been active, in the order first shown — derived during render (React's
  // documented pattern), so a pane shown once stays mounted under `keepMounted` + `render`.
  const [shownKeys, setShownKeys] = useState<string[]>([activeKey]);
  let knownShownKeys: string[] = shownKeys;
  if (keepMounted && render !== undefined && !shownKeys.includes(activeKey)) {
    knownShownKeys = [...shownKeys, activeKey];
    setShownKeys(knownShownKeys);
  }
  // The direction is fixed when the swap starts and kept for as long as it is played: derived from
  // the two keys on every render instead, it would fall away with the leaver and restart the
  // incoming pane's animation on a different keyframe. Kept-mounted panes never leave — the hook
  // then only says whether a swap has happened and which way it went.
  const swap: IPaneSwap<KuikaTabContentDirection | null> = usePaneSwap<KuikaTabContentDirection | null>(
    activeKey,
    !prefersReducedMotion && !keepMounted,
    (fromKey: string, toKey: string): KuikaTabContentDirection | null => directionOf(order, fromKey, toKey)
  );

  const renderPane = (key: string): React.ReactNode => (render !== undefined ? render(key) : findPane(children, key));

  const directionClass: string | undefined =
    swap.detail !== null ? `kuika-tab-content__pane--${swap.detail}` : undefined;
  const incomingClassName: string = classNames(
    "kuika-tab-content__pane",
    swap.hasSwapped && "kuika-tab-content__pane--in",
    swap.hasSwapped && directionClass
  );

  if (keepMounted) {
    const panes: IKeyedPane[] =
      render !== undefined ? renderedPanes(render, order, knownShownKeys) : keyedChildren(children);

    return (
      <div className={classNames("kuika-tab-content", className)}>
        {panes.map((pane: IKeyedPane): JSX.Element => {
          const isActive: boolean = pane.key === activeKey;
          return (
            <div
              key={pane.key}
              className={isActive ? incomingClassName : "kuika-tab-content__pane"}
              hidden={!isActive}
              aria-hidden={isActive ? undefined : "true"}
            >
              {pane.node}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={classNames("kuika-tab-content", className)}>
      {swap.leaverKey !== null && (
        <div
          key={swap.leaverKey}
          className={classNames("kuika-tab-content__pane", "kuika-tab-content__pane--out", directionClass)}
          aria-hidden="true"
          onAnimationEnd={swap.onLeaverAnimationEnd}
        >
          {renderPane(swap.leaverKey)}
        </div>
      )}
      <div key={activeKey} className={incomingClassName}>
        {renderPane(activeKey)}
      </div>
    </div>
  );
};

export default KuikaTabContent;
